/**
 * The `createArtifact` wire contract, shared by the server, the client renderer and the eval
 * harness. Lifecycle events with `documentId` at the top level on every variant, so
 * `getStudioPartDocumentId` needs no per-tool branch.
 */
import { type IArtifactReceipt, receiptOwesTeacherNews } from '../studio/artifactReceipt';
import { STUDIO_TOOL_TYPES } from '../studio/constants';

export const CREATE_ARTIFACT_TOOL_NAME = 'createArtifact';

export const CREATE_ARTIFACT_PART_TYPE: typeof STUDIO_TOOL_TYPES.CREATE_ARTIFACT = `tool-${CREATE_ARTIFACT_TOOL_NAME}`;

/** What the model says it is making; code maps it onto an engine. */
export type ArtifactKind = 'document' | 'presentation';

/**
 * `deck` runs in the browser: the server makes nothing and writes its client call instead. No engine
 * writes `skill-worker` any more; stored messages still carry it.
 */
export type ArtifactEngineId = 'skill-worker' | 'markdown' | 'deck';

/** The engines that still run, so the ids a new part can carry. */
export type LiveArtifactEngineId = Exclude<ArtifactEngineId, 'skill-worker'>;

/** Why a slides ask went to the markdown writer: the turn did not register the deck tools. */
export type ArtifactEngineReason = 'no-deck-engine';

export const ARTIFACT_TITLE_MAX_LENGTH = 100;

/** The deck engine's client-resolved tool. Never on Raina's menu: its call is written for her. */
export const ARTIFACT_DECK_HANDOFF_TOOL = 'createPresentation';

/** The id of the client call written for a `createArtifact` call, so history can pair the two. */
export const clientToolCallIdFor = (artifactToolCallId: string): string => `${artifactToolCallId}-client`;

/** The client-resolved tool call a browser-run engine makes its artifact through; the turn's stream writes it. */
export interface ArtifactClientCall {
  readonly toolName: string;
  readonly toolCallId: string;
  readonly input: Readonly<Record<string, unknown>>;
}

/** The document fields shared with the retired `createSubAgent`'s stored events, so the client accessors read both parts the same way. */
interface ArtifactDocumentFields {
  readonly documentId?: string;
  readonly metadata?: Readonly<Record<string, string>>;
  readonly blockCount?: number;
  readonly textLength?: number;
}

export interface ArtifactStartedOutput extends ArtifactDocumentFields {
  readonly kind: 'started';
  readonly status: 'running';
  readonly title: string;
  readonly engine: ArtifactEngineId;
}

export interface ArtifactOkOutput extends ArtifactDocumentFields {
  readonly kind: 'finished';
  readonly status: 'ok';
  readonly documentId: string;
  readonly title: string;
  readonly engine: ArtifactEngineId;
  /** A handle, never the body: the document is re-injected into later turns the way `createDocument`'s are. */
  readonly digest: string;
  /** What fell short, as facts; the digest narrates it. Absent on parts persisted before it existed. */
  readonly receipt?: IArtifactReceipt;
  /** Set as a pair on a cut-off run: still `ok`, the card renders what was kept. */
  readonly partial?: true;
  readonly partialReason?: string;
}

export interface ArtifactErrorOutput extends ArtifactDocumentFields {
  readonly kind: 'finished';
  readonly status: 'error';
  readonly title: string;
  readonly engine?: ArtifactEngineId;
  readonly reason: string;
}

/** The turn's one document is already taken; the model is steered to edit it. */
export interface ArtifactSteerOutput {
  readonly kind: 'finished';
  readonly status: 'steer';
  readonly documentId?: string;
  readonly title: string;
  readonly message: string;
  /** Nothing was written, so the lost-work numerator must not count it. */
  readonly refused: true;
}

/**
 * An ask a browser-run engine makes (today, slides). Nothing is made on the server; the stream
 * writes `clientCall`, the client builds the artifact from it, and the turn ends.
 */
export interface ArtifactHandoffOutput {
  readonly kind: 'finished';
  readonly status: 'handoff';
  readonly title: string;
  readonly engine: 'deck';
  readonly clientCall: ArtifactClientCall;
}

export type CreateArtifactOutput =
  | ArtifactStartedOutput
  | ArtifactOkOutput
  | ArtifactErrorOutput
  | ArtifactSteerOutput
  | ArtifactHandoffOutput;

/**
 * What the model reads back from a `createArtifact` result, this turn and every later one. The
 * engine and the size fields stay on the persisted part for the client and the analytics; the model
 * gets none of them, because the prompt forbids talking about tools or plugins.
 */
const MODEL_VISIBLE_FIELDS = [
  'status',
  'documentId',
  'title',
  'digest',
  'partial',
  'message',
  'reason',
  // A deck's build report, folded in from the client call the model never made.
  'deckId',
  'deckTitle',
  'slideCount',
  'outline',
] as const;

export function artifactModelView(output: unknown): unknown {
  if (typeof output !== 'object' || output === null) return output;
  const record = output as Record<string, unknown>;
  return Object.fromEntries(MODEL_VISIBLE_FIELDS.filter((field) => record[field] !== undefined).map((field) => [field, record[field]]));
}

/** Structural, not a schema parse: persisted threads hold outputs from every version this tool ever had. */
export function isArtifactDelivered(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) return false;
  const record = output as { status?: unknown; kind?: unknown };
  return record.kind === 'finished' && record.status === 'ok';
}

export const ASK_PENDING_BOUNCE_KIND = 'ask-pending-bounce';

/** What the ask guard returns in place of a build requested alongside a pending question card; nothing was made. */
export interface AskPendingBounce {
  readonly kind: typeof ASK_PENDING_BOUNCE_KIND;
  readonly message: string;
}

export const isAskPendingBounce = (value: unknown): value is AskPendingBounce =>
  typeof value === 'object' && value !== null && (value as { readonly kind?: unknown }).kind === ASK_PENDING_BOUNCE_KIND;

export function isArtifactHandoff(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) return false;
  const record = output as { status?: unknown; kind?: unknown };
  return record.kind === 'finished' && record.status === 'handoff';
}

export function isArtifactPart(part: { type: string; state?: string; output?: unknown }): boolean {
  return part.type === CREATE_ARTIFACT_PART_TYPE && part.state === 'output-available' && isArtifactDelivered(part.output);
}

/** A part that only handed the turn to the deck builder: nothing to render, nothing to count. */
export function isArtifactHandoffPart(part: { type: string; state?: string; output?: unknown }): boolean {
  return part.type === CREATE_ARTIFACT_PART_TYPE && part.state === 'output-available' && isArtifactHandoff(part.output);
}

type StepSlice = { readonly toolResults?: readonly { readonly toolName: string; readonly output?: unknown }[] } | undefined;

const receiptOf = (output: unknown): IArtifactReceipt | undefined =>
  typeof output === 'object' && output !== null ? (output as { receipt?: IArtifactReceipt }).receipt : undefined;

/**
 * The stop condition's test: the turn ends once the artifact is in a document, unless its receipt
 * still owes the teacher news (it stopped partway, figures were not drawn, a standard was not
 * verified, formatting was not applied).
 */
export function stepProducedArtifact(step: StepSlice): boolean {
  return (
    step?.toolResults?.some(
      (result) =>
        result.toolName === CREATE_ARTIFACT_TOOL_NAME &&
        isArtifactDelivered(result.output) &&
        !receiptOwesTeacherNews(receiptOf(result.output)),
    ) ?? false
  );
}

/** The stop condition's test for a browser-run engine: its client call is written, and the client answers it. */
export function stepHandedOffToDeck(step: StepSlice): boolean {
  return step?.toolResults?.some((result) => result.toolName === CREATE_ARTIFACT_TOOL_NAME && isArtifactHandoff(result.output)) ?? false;
}
